<template>
  <div
    v-if="data.activateEvents"
    class="container pt-0 pb-8 min-h-screen events-section lg:py-9"
  >
    <div class="grid-main">
      <div
        class="col-span-12"
        v-if="isLoading"
      >
        <p class="text-center animate-pulse">loading...</p>
      </div>
      <!-- Download -->
      <div class="col-span-12 lg:col-span-4">
        <div
          v-if="data.eventFile"
          class="flex justify-between py-1 border-b-[0.5px] mb-10 lg:mb-15 cursor-pointer items-center"
        >
          <SanityFile :asset-id="data.eventFile.asset._ref">
            <template #default="{ src }">
              <a
                class="flex justify-between items-center w-full no-underline transition-all duration-300 hover:translate-x-1 ease-in-dino"
                target="_blank"
                :href="src"
              >
                <p>{{ data.eventFileLabel }} (PDF)</p>
                <span class="download-icon">
                  <svg
                    width="64"
                    height="64"
                    viewBox="0 0 64 64"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M32.1875 38.1072L32.1875 14"
                      stroke="#333333"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M22 28.0625L32 38.0626L42 28.0625"
                      stroke="#333333"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <line
                      x1="17.75"
                      y1="45.3125"
                      x2="48.25"
                      y2="45.3125"
                      stroke="#333333"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
              </a>
            </template>
          </SanityFile>
        </div>
      </div>

      <!-- Filters -->
      <div class="col-span-12"></div>

      <!-- Event location -->
      <div class="col-span-12 event-radio">
        <label class="form-control">
          <input
            v-model="regionFilter"
            type="radio"
            name="radio"
            :value="FilterType.ONLY_WALDHAUS"
          />
          {{ $text('event-filter-all') }}
        </label>

        <label class="form-control">
          <input
            v-model="regionFilter"
            type="radio"
            name="radio"
            :value="FilterType.ALL_ACTIVITIES"
          />
          {{ $text('event-filter-all-region') }}
        </label>
      </div>

      <!-- Category Filter -->
      <div class="col-span-12 lg:col-span-5 select">
        <FormKit
          v-model="categoryActiveFilter"
          type="select"
          name="category"
          :options="categoryFilters"
        />
      </div>

      <!-- Month Filter -->
      <div class="col-span-12 lg:col-span-5 select">
        <FormKit
          v-model="monthActiveFilter"
          type="select"
          name="months"
          :options="monthsFilter"
          placeholder="Select month..."
          @input="(value) => console.log('FormKit month input:', value)"
        />
      </div>

      <!-- Submit Button (kept for manual refresh, but filtering is now automatic) -->
      <div class="col-span-12 lg:col-span-2 select">
        <Button
          :variant="'primary'"
          :variantType="'normal'"
          @click="submitSearch"
          >{{ $text('event-search-button') }}
        </Button>
      </div>
    </div>

    <div id="event-list">
      <!-- Results preview amount -->
      <div
        class="flex mt-8 mb-2 lg:mt-6"
        v-if="searchResultList.length !== 0"
      >
        <Transition
          mode="out-in"
          appear
          name="slide"
        >
          <span
            v-if="searchResultList && searchResultList.length > 0"
            :key="`results-${searchResultList.length}-${categoryActiveFilter}-${regionFilter}-${monthActiveFilter}`"
            class="mr-1 result-length h4"
            >{{ searchResultList.length }}</span
          >
        </Transition>
        <h4>{{ $text('event-result') }}</h4>
      </div>

      <!-- Events (rendered incrementally on scroll) -->
      <template
        v-for="(item, i) in visibleEvents"
        :key="
          item
            ? `${item.sanityId}-${item.cleanDate.startDate}-${item.cleanDate.startTime}-${i}`
            : `empty-${i}`
        "
      >
        <EventItem
          v-if="item"
          :item="item"
          :event-page-path="eventPagePath"
        />
      </template>

      <div
        v-if="hasMoreEvents"
        ref="loadMoreSentinel"
        class="h-px"
        aria-hidden="true"
      />
    </div>

    <!-- No Results -->
    <div
      v-if="!searchResultList || searchResultList.length === 0"
      class="mt-10"
    >
      <h4>{{ $text('event-no-results') }}</h4>
    </div>
  </div>
  <div v-else>
    <p>Event Section is deactivated.</p>
  </div>
</template>

<script setup lang="ts">
import { useEventsStore } from '~/stores/useEventsStore';
import { storeToRefs } from 'pinia';
import { FilterType } from '~/types';
import { localeCodes } from '.nuxt/i18n.options.mjs';

const { data } = defineProps<{
  data: {
    activateEvents: boolean;
    eventFileLabel: string;
    eventFile: {
      asset: {
        _ref: string;
      };
    };
  };
}>();

// Store and Composables
const { locale } = useI18n();
const { $text } = useSiteSettings();
const eventsStore = useEventsStore();
const isLoading = ref(true);
await eventsStore.fetchEventsFromApi();
// Async ops
isLoading.value = false;
// const nuxtApp = useNuxtApp();
// const resizeLenis = nuxtApp.$resizeLenis;
// resizeLenis();
const { allEvents, defaultEvents, categoryFilters, monthsFilter } = storeToRefs(eventsStore);

// Refs
const currentMonth = new Date().getMonth() + 1;
const currentYear = new Date().getFullYear();
const categoryActiveFilter = ref<string | undefined>('');
const regionFilter = ref<FilterType>(FilterType.ONLY_WALDHAUS);
const monthActiveFilter = ref<string | undefined>();
const activeFilters = ref<Array<string | undefined>>([]);
const searchResultList = ref<any[]>([]);
const route = useRoute();
const eventPagePath = route.path;
const {
  visibleItems: visibleEvents,
  hasMore: hasMoreEvents,
  sentinelRef: loadMoreSentinel
} = useIncrementalRender(searchResultList);

// Initialize month filter when monthsFilter becomes available
watchEffect(() => {
  if (monthsFilter.value && monthsFilter.value.length > 0 && !monthActiveFilter.value) {
    // Find the current month in the available options, or default to first option
    const currentMonthValue = `${currentMonth}-${currentYear}`;
    const matchingOption = monthsFilter.value.find((option) => option.value === currentMonthValue);

    monthActiveFilter.value = matchingOption ? currentMonthValue : monthsFilter.value[0].value;

    console.log('Initialized month filter:', {
      currentMonthValue,
      matchingOption,
      finalValue: monthActiveFilter.value,
      availableOptions: monthsFilter.value.map((o) => ({ label: o.label, value: o.value }))
    });
  }
});

const submitSearch = () => {
  // Don't filter if data is still loading
  if (!allEvents.value || allEvents.value.length === 0) {
    console.log('No events available for filtering');
    searchResultList.value = [];
    return;
  }

  const isCategoryFilterActive = categoryActiveFilter.value === '';
  const isMonthFilterActive = !monthActiveFilter.value || monthActiveFilter.value === '';
  const onlyWaldhausEvents = regionFilter.value === FilterType.ONLY_WALDHAUS;

  // Build active filters array
  activeFilters.value = [
    onlyWaldhausEvents ? 'channels-waldhaus-sils-ch' : undefined,
    isCategoryFilterActive ? undefined : categoryActiveFilter.value,
    isMonthFilterActive ? undefined : monthActiveFilter.value
  ].filter((filter) => filter !== undefined);

  // If no specific filters are active, show all current events
  if (activeFilters.value.length === 0) {
    searchResultList.value =
      allEvents.value?.filter((item) => {
        if (!item) return false;

        // Filter out past events
        const eventDate = new Date(item.cleanDate.startDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        eventDate.setHours(0, 0, 0, 0);

        return eventDate >= today;
      }) || [];
    return;
  }

  // Apply filters
  const filteredEvents =
    allEvents.value?.filter((item) => {
      if (!item) return false;

      // Filter out past events
      const eventDate = new Date(item.cleanDate.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      eventDate.setHours(0, 0, 0, 0);

      if (eventDate < today) return false;

      // item has to contain all the activeFilters to be returned
      return activeFilters.value.every((filter) => {
        if (typeof filter === 'string') {
          // Check if it's a month-year filter (format: "1-2025")
          if (filter.includes('-') && filter.match(/^\d{1,2}-\d{4}$/)) {
            const [selectedMonth, selectedYear] = filter.split('-').map(Number);

            // Validate month and year
            if (!selectedMonth || !selectedYear || selectedMonth < 1 || selectedMonth > 12) {
              console.warn('Invalid month-year filter:', filter);
              return false;
            }

            // Create start and end of the selected month
            const monthStart = new Date(selectedYear, selectedMonth - 1, 1);
            const monthEnd = new Date(selectedYear, selectedMonth, 0, 23, 59, 59, 999);

            // Get event start and end dates
            const eventStart = new Date(item.cleanDate.startDate);
            const eventEnd = item.cleanDate.endDate ? new Date(item.cleanDate.endDate) : eventStart;

            // Validate event dates
            if (isNaN(eventStart.getTime()) || isNaN(eventEnd.getTime())) {
              console.warn('Invalid event dates:', {
                startDate: item.cleanDate.startDate,
                endDate: item.cleanDate.endDate
              });
              return false;
            }

            // Check if event overlaps with the selected month
            return eventStart <= monthEnd && eventEnd >= monthStart;
          } else {
            // Handle other string filters (categories, regions, etc.)
            return item.listFacetIdAttribute.includes(filter);
          }
        }
        return false;
      });
    }) || [];

  // Update the search results
  searchResultList.value = filteredEvents;
};

// Make filtering reactive by watching for changes
watch(
  [categoryActiveFilter, regionFilter, monthActiveFilter, allEvents],
  (newValues, oldValues) => {
    // Only run if we have events to filter
    if (allEvents.value && allEvents.value.length > 0) {
      submitSearch();
    } else {
      console.log('Skipping search - no events available');
      searchResultList.value = [];
    }
  },
  { immediate: true }
);
</script>

<style lang="postcss">
.events-section {
  .event-line {
    @apply transition-all duration-300;
    &:hover {
      .arrow-event {
        @apply bg-green;

        svg {
          path {
            @apply !stroke-white transition-all duration-700;
          }
        }
      }
    }
  }

  .slide-enter-active {
    animation: slide-fade-in 0.5s cubic-bezier(0.075, 0.82, 0.165, 1);
  }

  .slide-leave-active {
    animation: slide-fade-out 0.3s cubic-bezier(0.075, 0.82, 0.165, 1);
  }

  @keyframes slide-fade-in {
    0% {
      transform: translateY(100%);
      opacity: 0;
    }
    100% {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes slide-fade-out {
    0% {
      transform: translateY(0);
      opacity: 1;
    }
    100% {
      transform: translateY(-50%);
      opacity: 0;
    }
  }
}

.event-radio {
  @apply mb-4 lg:mb-10 lg:flex lg:items-center;

  .form-control {
    display: grid;
    grid-template-columns: 1em auto;
    gap: 0.5em;
    @apply mb-4 lg:mb-0 lg:mr-15;
  }

  .form-control:focus-within {
    color: var(--form-control-color);
  }
  input[type='radio'] {
    /* Add if not using autoprefixer */
    -webkit-appearance: none;
    /* Remove most all native input styles */
    -moz-appearance: none;
    appearance: none;
    /* For iOS < 15 */
    background-color: var(--form-background);
    /* Not removed via appearance */
    margin: 0;
    font: inherit;
    color: currentColor;
    width: 1.15em;
    height: 1.15em;
    border: 0.02em solid currentColor;
    border-radius: 50%;
    transform: translateY(-0.075em);
    display: grid;
    place-content: center;
  }

  input[type='radio']::before {
    content: '';
    width: 0.35em;
    height: 0.35em;
    border-radius: 50%;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    box-shadow: inset 1em 1em var(--form-control-color);
    /* Windows High Contrast Mode */
    background-color: CanvasText;
  }

  input[type='radio']:checked::before {
    transform: scale(1);
  }

  /* input[type='radio']:focus {
    outline: max(2px, 0.15em) solid currentColor;
    outline-offset: max(2px, 0.15em);
  } */
}

.select select.formkit-input {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
</style>
